import type { SignUpFormValues } from '../../schemas';

import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import {
  Asterisk,
  FormControl,
  FormField,
  FormHelper,
  FormItem,
  FormLabel,
} from '@paid-ui/components/form';
import { Input } from '@paid-ui/components/input';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { Tooltip } from '@paid-ui/components/tooltip';

import { trustTypeOptions } from '../../constants/business';
import { signUpManager } from '../../models';
import { SignUpStep, TrustType } from '../../models/enums';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepTrusteeDetails: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext<SignUpFormValues>();
  const { currentStep } = useSnapshot(signUpManager);
  const trustType = form.watch('trustType') as TrustType;

  const trustName = form.watch('trustName');
  const isValid = Boolean(trustName && trustType) && !form.getFieldState('trustName').invalid;

  const handleNext = () => {
    switch (trustType) {
      case TrustType.INDIVIDUAL:
      case TrustType.MULTIPLE_INDIVIDUALS:
      case TrustType.EMPLOYEE_OF_INDIVIDUALS: {
        form.setValue('isEmployee', trustType === TrustType.EMPLOYEE_OF_INDIVIDUALS);
        goToStep?.(SignUpStep.USER_DETAILS);
        break;
      }
      case TrustType.OFFICER_OF_COMPANY:
      case TrustType.EMPLOYEE_OF_COMPANY: {
        form.setValue('isEmployee', trustType === TrustType.EMPLOYEE_OF_COMPANY);
        goToStep?.(SignUpStep.TRUSTEE_COMPANY_DETAILS);
        break;
      }
      default:
    }
  };

  if (currentStep !== SignUpStep.TRUSTEE_DETAILS) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="trustName"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="Trust name" disabled={disabled} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
      </div>
      <FormField
        name="trustType"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Your role <Asterisk />
            </FormLabel>
            <FormControl>
              <RadioGroup
                {...field}
                onValueChange={(value) =>
                  form.setValue('trustType', value as TrustType, {
                    shouldValidate: true,
                  })
                }
                disabled={disabled}
                className="space-y-3.5"
              >
                {trustTypeOptions.map((option) => (
                  <Tooltip key={option.value} title={option.tooltip}>
                    <div>
                      <RadioGroupCard value={option.value}>{option.label}</RadioGroupCard>
                    </div>
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>
            <FormHelper />
          </FormItem>
        )}
      />
      <Button
        variant="solid"
        disabled={disabled || !isValid}
        className="h-[44px]"
        onClick={handleNext}
      >
        Continue
      </Button>
    </div>
  );
};
